import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import { Tooltip } from 'components/Widget/Tooltip';
import { Loader } from 'components/Widget/Loader';
import { DesignSystem } from 'dragonmouse-shared/interfaces';
import { useRef, useState } from 'react';
import useOnClickOutside from 'hooks/useOnClickOutside';
import { shallowEqual, useSelector } from 'react-redux';
import {
	selectActiveScene,
	selectActiveSceneDesignSystem,
	selectDesignSystems,
	selectIsFreeTier,
	selectActiveSceneLinkedDesignSystemVersion,
	selectActiveSceneLatestDesignSystemVersion,
} from 'redux/db/reducers';
import { canEdit } from 'redux/db/util';
import { RootReducer } from 'redux/reducers';
import { displayViewOnlyModal, displayUpgradeModal } from 'redux/mode/util';
import { useTheme } from 'styled-components';
import { useNavigate } from 'react-router';
import * as routes from 'routes';
import LinkPanel from './LinkPanel';
import ActiveDsPanel from './ActiveDsPanel';
import * as S from './styles';

interface DsControlProps {
	compactMode?: 'full' | 'compact';
}

const DsControl = ({ compactMode = 'full' }: DsControlProps) => {
	const theme = useTheme();
	const navigate = useNavigate();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [showLinkPanel, setShowLinkPanel] = useState(false);
	const [isLinking, setIsLinking] = useState<string | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLButtonElement>(null);
	const [tooltipId] = useState(() => `ds-control-tooltip-${Math.random()}`);
	const [dropdownPosition, setDropdownPosition] = useState<'above' | 'below'>('above');

	const { scene, designSystems, activeDesignSystem, linkedVersion, latestVersion, isFreeTier } =
		useSelector((r: RootReducer) => {
			return {
				scene: selectActiveScene(r.db, r.mode.activeSceneId),
				designSystems: selectDesignSystems(r.db),
				activeDesignSystem: selectActiveSceneDesignSystem(r.db, r.mode.activeSceneId),
				linkedVersion: selectActiveSceneLinkedDesignSystemVersion(r.db),
				latestVersion: selectActiveSceneLatestDesignSystemVersion(r.db),
				isFreeTier: selectIsFreeTier(r.db),
			};
		}, shallowEqual);

	// Check if we have an active design system
	const actualHasActiveDs = !!activeDesignSystem;
	const dsCount = 1; // Placeholder - will be derived from metadata when available

	// Calculate status based on versions
	const dsStatus: 'latest' | 'publishing' | 'updateAvailable' | null = (() => {
		if (!linkedVersion || !latestVersion) return null;
		if (linkedVersion.versionNumber === latestVersion.versionNumber) {
			// Check if latest is still publishing
			const promptStatus = latestVersion.manifest?.promptGenerationStatus;
			if (promptStatus === 'pending' || promptStatus === 'generating') {
				return 'publishing';
			}
			return 'latest';
		}
		// Check if latest is ready for use
		const promptStatus = latestVersion.manifest?.promptGenerationStatus;
		if (promptStatus === 'complete') {
			return 'updateAvailable';
		}
		if (promptStatus === 'pending' || promptStatus === 'generating') {
			return 'publishing';
		}
		return null;
	})();

	const displayHasActiveDs = actualHasActiveDs;

	// Close dropdown when clicking outside
	useOnClickOutside(dropdownRef, () => {
		if (isDropdownOpen) {
			setIsDropdownOpen(false);
			setShowLinkPanel(false);
		}
	});

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const calculateDropdownPosition = () => {
		if (!buttonRef.current) return 'above';

		const buttonRect = buttonRef.current.getBoundingClientRect();
		const viewportHeight = window.innerHeight;
		const dropdownHeight = 300; // Approximate height of dropdown (can be adjusted)
		const gap = 8; // Gap between button and dropdown

		const spaceAbove = buttonRect.top;
		const spaceBelow = viewportHeight - buttonRect.bottom;

		// If we can fit above without overflowing, prefer above
		if (spaceAbove >= dropdownHeight + gap) {
			return 'above';
		}
		// If we can fit below without overflowing, use below
		if (spaceBelow >= dropdownHeight + gap) {
			return 'below';
		}
		// Default to above if neither fits perfectly (but prefer above for consistency)
		return 'above';
	};

	const onToggleDropdown = () => {
		const newIsOpen = !isDropdownOpen;
		if (newIsOpen) {
			// Calculate position when opening
			setDropdownPosition(calculateDropdownPosition());
		}
		setIsDropdownOpen(newIsOpen);
		// Reset link panel visibility when closing dropdown
		if (!newIsOpen) {
			setShowLinkPanel(false);
		}
	};

	const onLinkDs = async (designSystem: DesignSystem | null) => {
		if (!scene || !canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}

		// Hide dropdown immediately and show linking state
		setIsDropdownOpen(false);
		setShowLinkPanel(false);
		setIsLinking(designSystem ? 'Linking...' : 'Unlinking...');

		try {
			// Link or unlink design system (null to unlink)
			await forge.linkSceneDesignSystem({
				sceneId: scene.id,
				designSystemId: designSystem?.id ?? null,
			});
		} catch (error) {
			// Error is already handled by alertOnError wrapper
			console.error('Failed to update design system link:', error);
		} finally {
			setIsLinking(null);
		}
	};

	const onCreateNew = () => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		if (isFreeTier) {
			displayUpgradeModal('Creating design systems');
			return;
		}
		setIsDropdownOpen(false);
		navigate(routes.createDesignSystem(scene?.id));
	};

	// Determine when to show tooltip:
	// 1. Empty state: Show tooltip to guide user to add first design system
	// 2. Compact mode: Show tooltip with design system title since text is hidden
	const shouldShowTooltip = !isLinking && (!displayHasActiveDs || compactMode === 'compact');
	const tooltipContent = !displayHasActiveDs
		? 'Click to add first design system'
		: compactMode === 'compact' && activeDesignSystem
		? activeDesignSystem.title
		: undefined;

	return (
		<S.DropdownContainer ref={dropdownRef}>
			<S.DsControl
				ref={buttonRef}
				isEmpty={!displayHasActiveDs}
				isActive={displayHasActiveDs}
				isLoading={dsStatus === 'publishing' || !!isLinking}
				compactMode={compactMode}
				onClick={onToggleDropdown}
				data-tooltip-id={shouldShowTooltip ? tooltipId : undefined}
				data-tooltip-content={tooltipContent}
			>
				{isLinking ? (
					<>
						<Loader width={12} height={12} />
						<S.DsText>{isLinking}</S.DsText>
					</>
				) : displayHasActiveDs ? (
					<>
						{dsStatus === 'latest' || dsStatus === null ? (
							<S.DsStatusContainer>
								<Icon type={IconType.STACK} width={14} height={14} />
							</S.DsStatusContainer>
						) : null}
						{dsStatus === 'publishing' && <Loader width={12} height={12} />}
						{dsStatus === 'updateAvailable' && (
							<S.DsStatusContainer>
								<Icon type={IconType.REFRESH} width={12} height={12} color={theme.blue[500]} />
							</S.DsStatusContainer>
						)}
						{compactMode === 'full' && (
							<S.DsText>{activeDesignSystem?.title || 'Design System'}</S.DsText>
						)}
						{dsCount > 1 && <S.DsBadge>{dsCount}</S.DsBadge>}
						<S.DsChevron>
							<Icon
								type={IconType.FILLED_ARROW_DOWN}
								width={10}
								height={10}
								color={theme.gray[600]}
							/>
						</S.DsChevron>
					</>
				) : (
					<>
						<S.DsIcon>
							<Icon type={IconType.STACK} width={14} height={14} />
						</S.DsIcon>
						<S.DsText>Add DS</S.DsText>
					</>
				)}
			</S.DsControl>
			{shouldShowTooltip && <Tooltip id={tooltipId} placement="top" />}
			<S.Dropdown isOpen={isDropdownOpen && !isLinking} position={dropdownPosition}>
				{displayHasActiveDs && !showLinkPanel ? (
					<ActiveDsPanel
						onLinkDs={onLinkDs}
						onClose={() => setIsDropdownOpen(false)}
						onShowLinkPanel={() => setShowLinkPanel(true)}
					/>
				) : (
					<LinkPanel designSystems={designSystems} onCreateNew={onCreateNew} onLinkDs={onLinkDs} />
				)}
			</S.Dropdown>
		</S.DropdownContainer>
	);
};

export default DsControl;
